// WebSocket to the API with reconnect (AD-31). The browser's WebSocket in the app, the `ws` package in Node tests.
import { WS_CLOSE, parseServerMessage, type ClientMessage, type ServerMessage } from '@brewcore/app-contract';

export interface SocketLike {
  readonly readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev: unknown) => void) | null;
  onclose: ((ev: { code?: number }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
}

export type SocketFactory = (url: string) => SocketLike;

/** ok: connected; cloud: the API is not reachable; device: this phone/browser has no network (FR-14). */
export type NetState = 'connecting' | 'ok' | 'cloud_unreachable' | 'device_offline';

export interface ConnectionOptions {
  url: string;
  factory: SocketFactory;
  /** navigator.onLine in the browser */
  isOnline?: () => boolean;
  minBackoffMs?: number;
  maxBackoffMs?: number;
}

export interface ConnectionHandlers {
  onOpen(): void;
  onMessage(msg: ServerMessage): void;
  onClose(): void;
  onNetState(state: NetState): void;
  /** The API closed with 4401 (no or ended session) or 4403 (foreign origin): no reconnect until start(). */
  onAuthLost?(code: number): void;
}

const OPEN = 1;

export class Connection {
  private socket: SocketLike | null = null;
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private closed = false;
  private netState: NetState = 'connecting';

  constructor(
    private readonly opts: ConnectionOptions,
    private readonly handlers: ConnectionHandlers,
  ) {}

  get state(): NetState {
    return this.netState;
  }

  get isOpen(): boolean {
    return this.socket?.readyState === OPEN;
  }

  start(): void {
    this.closed = false;
    this.open();
  }

  private setNet(state: NetState): void {
    if (state === this.netState) return;
    this.netState = state;
    this.handlers.onNetState(state);
  }

  private open(): void {
    if (this.closed) return;
    let socket: SocketLike;
    try {
      socket = this.opts.factory(this.opts.url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;
    socket.onopen = () => {
      this.attempt = 0;
      this.setNet('ok');
      this.handlers.onOpen();
    };
    socket.onmessage = (ev) => {
      if (typeof ev.data !== 'string') return;
      let json: unknown;
      try {
        json = JSON.parse(ev.data);
      } catch {
        return;
      }
      const parsed = parseServerMessage(json);
      if (parsed.ok) this.handlers.onMessage(parsed.value);
    };
    socket.onerror = () => undefined;
    socket.onclose = (ev) => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.handlers.onClose();
      if (ev?.code === WS_CLOSE.unauthorized || ev?.code === WS_CLOSE.badOrigin) {
        this.closed = true;
        this.handlers.onAuthLost?.(ev.code);
        return;
      }
      this.setNet(this.opts.isOnline && !this.opts.isOnline() ? 'device_offline' : 'cloud_unreachable');
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    if (this.closed) return;
    const min = this.opts.minBackoffMs ?? 500;
    const max = this.opts.maxBackoffMs ?? 10_000;
    const delay = Math.min(max, min * 2 ** this.attempt) * (0.75 + Math.random() * 0.5);
    this.attempt += 1;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => this.open(), delay);
  }

  send(msg: ClientMessage): boolean {
    if (!this.socket || this.socket.readyState !== OPEN) return false;
    this.socket.send(JSON.stringify(msg));
    return true;
  }

  /** Drops the connection (tests: network break); reconnects with backoff. */
  drop(): void {
    this.socket?.close(4000, 'drop');
  }

  /** Reconnect now (e.g. the app came back to the foreground). */
  reconnectNow(): void {
    if (this.isOpen) return;
    if (this.timer) clearTimeout(this.timer);
    this.attempt = 0;
    this.open();
  }

  close(): void {
    this.closed = true;
    if (this.timer) clearTimeout(this.timer);
    const s = this.socket;
    this.socket = null;
    s?.close(1000, 'bye');
  }
}
